// src/components/insights/InsightAnswer.js — one answer in the Ask Insights
// conversation: what-I'm-doing steps, the result card(s), a clarifying
// question, or the honest "I can't see that".
//
// Everything numeric renders straight from the measure result the server sent.
// The only model-written text is `sentence`, which the server has already
// checked against these same numbers.
// Module-scope components; hover via useState (no inline DOM mutation).

import React, { useEffect, useRef, useState } from 'react';
import MilestoneBadge from '../MilestoneBadge';
import { ChartFor } from './InsightCharts';
import { T, fmt, fmtParts, prettyDayKey } from './insightsFormat';
import { tableToCsv } from '../../services/askInsights';

// ── small parts ──
export function Chip({ children, onClick, tag, size = 'md', style }) {
    const [h, setH] = useState(false);
    return (
        <button type="button" onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
            style={{
                font: 'inherit', fontFamily: T.sans, fontSize: size === 'sm' ? 12.5 : 13, color: T.ink, cursor: 'pointer', textAlign: 'left',
                background: h ? '#fff' : 'rgba(255,255,255,0.62)', border: `1px solid ${h ? 'color-mix(in srgb, var(--mom-primary) 35%, transparent)' : T.line}`,
                padding: size === 'sm' ? '7px 12px' : '9px 14px', borderRadius: 99, boxShadow: h ? T.shSm : 'none', transition: 'all .15s',
                lineHeight: 1.35, ...style,
            }}>
            {tag && <b style={{ color: T.faint, fontWeight: 600, marginRight: 7, fontSize: 10.5, letterSpacing: '0.07em', textTransform: 'uppercase' }}>{tag}</b>}
            {children}
        </button>
    );
}

export function ActButton({ children, onClick, primary, disabled, title }) {
    const [h, setH] = useState(false);
    return (
        <button type="button" onClick={onClick} disabled={disabled} title={title} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
            style={{
                font: 'inherit', fontFamily: T.sans, fontSize: 12.5, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 13px', borderRadius: 12,
                background: primary ? 'var(--mom-charcoal)' : '#fff', color: primary ? '#fff' : h ? 'var(--mom-primary)' : T.ink,
                border: `1px solid ${primary ? 'var(--mom-charcoal)' : h ? 'color-mix(in srgb, var(--mom-primary) 40%, transparent)' : T.line}`,
                opacity: disabled ? 0.55 : 1, transform: h && !disabled ? 'translateY(-1px)' : 'none', transition: 'all .15s',
            }}>
            {children}
        </button>
    );
}

export const PinGlyph = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16 3l5 5-3 1-4 4 1 5-2 2-4-4-5 5-1-1 5-5-4-4 2-2 5 1 4-4z" /></svg>
);
const Check = ({ size = 10 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
);

export function DeltaPill({ value, format, label, good }) {
    if (value == null) return null;
    const flat = Number(value) === 0;
    const up = Number(value) > 0;
    const tone = flat ? 'flat' : good ? 'good' : 'bad';
    const colors = { good: [T.good, T.goodBg], bad: [T.bad, T.badBg], flat: [T.soft, T.well] }[tone];
    const text = format === 'pts' ? fmt(Math.abs(value), 'decimal') + ' pts' : fmt(Math.abs(value), format === 'signed' ? 'int' : format);
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: T.sans, fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 99, color: colors[0], background: colors[1], whiteSpace: 'nowrap' }}>
            {flat ? '—' : up ? '▲' : '▼'} {text}{label ? ` ${label}` : ''}
        </span>
    );
}

// ── steps ──
export function Steps({ steps, done }) {
    if (!steps || !steps.length) return null;
    return (
        <div style={{ background: 'rgba(255,255,255,0.66)', border: '1px solid rgba(255,255,255,0.9)', borderRadius: 20, padding: '12px 18px', boxShadow: T.shSm, maxWidth: 560, marginBottom: 14 }}>
            {steps.map((s, i) => {
                const running = s.state === 'run' && !done && i === steps.length - 1;
                return (
                    <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '4px 0', fontFamily: T.sans, fontSize: 13, lineHeight: 1.45, color: running ? T.ink : T.soft, animation: 'momInsFade .25s ease both' }}>
                        <span style={{ flex: '0 0 auto', width: 18, height: 18, marginTop: 1, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            ...(running ? { border: '2px solid #f0d6d4', borderTopColor: 'var(--mom-primary)', animation: 'spin .8s linear infinite' }
                                : s.state === 'note' ? { background: 'color-mix(in srgb, var(--mom-primary) 10%, #fff)', color: 'var(--mom-primary)', fontSize: 11, fontWeight: 700 }
                                    : { background: T.goodBg, color: T.good }) }}>
                            {!running && (s.state === 'note' ? 'i' : <Check />)}
                        </span>
                        <span>{s.text}</span>
                    </div>
                );
            })}
        </div>
    );
}

// ── tables ──
function cell(row, col) {
    const v = row[col.key];
    if (v == null || v === '') return col.empty || (col.format === 'day' ? '—' : '');
    if (col.format === 'day') return prettyDayKey(v);
    if (col.format) return fmt(v, col.format);
    return String(v);
}

const AVATAR_TONES = ['#c1848c', '#4f7c7a', '#b8913f', '#c05a7a', '#8a6f8f', '#7f9a7e'];
const initialsOf = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
const toneOf = (name) => AVATAR_TONES[(String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_TONES.length];

function DataTable({ table, onPerson }) {
    const [shown, setShown] = useState(10);
    const [hover, setHover] = useState(null);
    const [narrow, setNarrow] = useState(false);
    const boxRef = useRef(null);
    useEffect(() => {
        const el = boxRef.current;
        if (!el) return undefined;
        const check = () => setNarrow(el.getBoundingClientRect().width < 560);
        check();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(check); ro.observe(el);
        return () => ro.disconnect();
    }, []);
    if (!table || !table.rows || !table.rows.length) return null;
    const cols = table.columns || [];
    const people = table.kind === 'people';
    const isKV = cols.length === 2 && !cols[0].label && !cols[1].label;
    if (isKV) {
        return (
            <div style={{ padding: '6px 30px 4px' }}>
                {table.rows.map((r, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 16, padding: '8px 0', borderTop: i ? `1px solid ${T.hair}` : 'none', fontFamily: T.sans, fontSize: 13.5 }}>
                        <span style={{ color: T.faint, fontWeight: 600 }}>{r.k}</span><span style={{ color: T.ink }}>{r.v}</span>
                    </div>
                ))}
            </div>
        );
    }
    const rows = table.rows.slice(0, shown);
    const first = cols[0];
    const rest = cols.slice(1);
    const more = table.rows.length > shown && (
        <button type="button" onClick={() => setShown((x) => x + 25)} style={{ display: 'block', width: '100%', padding: 12, background: 'none', border: 'none', borderTop: `1px solid ${T.hair}`, cursor: 'pointer', fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.soft }}>
            Show {Math.min(25, table.rows.length - shown)} more · {table.rows.length.toLocaleString('en-US')} in total
        </button>
    );
    // Phone: two-line rows — name + the last column (usually the key date or
    // value) on top, every other column as a quiet line underneath.
    if (narrow) {
        const key = rest[rest.length - 1];
        const sub = rest.slice(0, -1);
        return (
            <div ref={boxRef} style={{ padding: '4px 12px 0' }}>
                {rows.map((r, i) => (
                    <div key={r.uid || i} onClick={people && r.uid && onPerson ? () => onPerson(r) : undefined}
                        style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '11px 8px', borderTop: i ? `1px solid ${T.hair}` : 'none', cursor: people && r.uid && onPerson ? 'pointer' : 'default' }}>
                        {people && <span aria-hidden="true" style={{ flex: '0 0 auto', width: 32, height: 32, borderRadius: '50%', background: toneOf(r.name), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.sans, fontSize: 11.5, fontWeight: 700 }}>{initialsOf(r.name)}</span>}
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontFamily: T.sans, fontSize: 14, color: T.ink }}>
                                <span style={{ fontWeight: people ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell(r, first)}</span>
                                {key && <span style={{ flex: '0 0 auto', fontWeight: 600 }}>{cell(r, key)}</span>}
                            </span>
                            <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px', alignItems: 'center', marginTop: 4, fontFamily: T.sans, fontSize: 12, color: T.faint }}>
                                {sub.map((c) => {
                                    if (c.key === 'plan') return <PlanTag key={c.key} plan={r.plan} />;
                                    const v = cell(r, c);
                                    return v ? <span key={c.key}>{c.label ? `${c.label}: ` : ''}{v}</span> : null;
                                })}
                            </span>
                        </span>
                    </div>
                ))}
                {more}
            </div>
        );
    }
    return (
        <div ref={boxRef} style={{ padding: '4px 16px 0', overflowX: 'auto' }}>
            <div style={{ minWidth: Math.min(640, 160 + rest.length * 110) }}>
                <div style={{ display: 'grid', gridTemplateColumns: `minmax(170px, 1.7fr) repeat(${rest.length}, minmax(80px, 1fr))`, gap: 12, padding: '8px 14px', fontFamily: T.sans, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.faint }}>
                    <span>{first.label}</span>{rest.map((c) => <span key={c.key} style={{ textAlign: c.format && c.format !== 'day' ? 'right' : 'left' }}>{c.label}</span>)}
                </div>
                {rows.map((r, i) => (
                    <div key={r.uid || i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                        onClick={people && r.uid && onPerson ? () => onPerson(r) : undefined}
                        style={{ display: 'grid', gridTemplateColumns: `minmax(170px, 1.7fr) repeat(${rest.length}, minmax(80px, 1fr))`, gap: 12, alignItems: 'center', padding: '10px 14px', borderRadius: 14, background: hover === i ? T.well : 'transparent', borderTop: i ? `1px solid ${T.hair}` : 'none', cursor: people && r.uid && onPerson ? 'pointer' : 'default', transition: 'background .15s' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                            {people && <span aria-hidden="true" style={{ flex: '0 0 auto', width: 34, height: 34, borderRadius: '50%', background: toneOf(r.name), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.sans, fontSize: 12, fontWeight: 700 }}>{initialsOf(r.name)}</span>}
                            <span style={{ minWidth: 0 }}>
                                <span style={{ display: 'block', fontFamily: T.sans, fontSize: 14, fontWeight: people ? 600 : 500, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell(r, first)}</span>
                                {people && r.email && <span style={{ display: 'block', fontFamily: T.sans, fontSize: 11.5, color: T.faint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email}</span>}
                            </span>
                        </span>
                        {rest.map((c) => (
                            <span key={c.key} style={{ fontFamily: T.sans, fontSize: 13, color: c.key === 'note' || c.key === 'reason' ? T.soft : T.ink, textAlign: c.format && c.format !== 'day' ? 'right' : 'left', fontWeight: c.format && c.format !== 'day' ? 600 : 400 }}>
                                {c.key === 'plan' ? <PlanTag plan={r.plan} /> : cell(r, c)}
                            </span>
                        ))}
                    </div>
                ))}
            </div>
            {more}
        </div>
    );
}

function PlanTag({ plan }) {
    if (!plan) return null;
    const p = String(plan);
    const tone = /Founding/.test(p) ? ['#9a7432', 'color-mix(in srgb, #b8913f 13%, #fff)']
        : /Yearly/.test(p) ? ['#3f6967', 'color-mix(in srgb, #4f7c7a 12%, #fff)']
            : /Ended|Not paying|Free/.test(p) ? [T.soft, T.well]
                : ['var(--mom-primary-dark, #c05a7a)', 'color-mix(in srgb, var(--mom-primary) 8%, #fff)'];
    return <span style={{ fontFamily: T.sans, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, color: tone[0], background: tone[1], whiteSpace: 'nowrap' }}>{p}</span>;
}

// ── proof ──
function Proof({ proof, asOf }) {
    if (!proof) return null;
    const rows = [
        ['Definition', proof.definition], ['The sum', proof.calc], ['Who counts', proof.included],
        ['Not counted', proof.excluded], ['Source', proof.source], ['Period', proof.period],
    ].filter(([, v]) => v);
    return (
        <div style={{ background: `linear-gradient(180deg, ${T.well}, var(--mom-cream))`, borderTop: `1px solid ${T.hair}`, padding: '20px 30px 22px', animation: 'momInsFade .2s ease both' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: T.sans, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.faint, marginBottom: 14 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" /><path d="M9 12l2 2 4-4" /></svg>
                How this was counted
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(96px, 140px) 1fr', gap: '9px 18px', fontFamily: T.sans, fontSize: 13, lineHeight: 1.55 }}>
                {rows.map(([k, v]) => (
                    <React.Fragment key={k}><span style={{ color: T.faint, fontWeight: 600 }}>{k}</span><span style={{ color: T.ink }}>{v}</span></React.Fragment>
                ))}
                {(proof.notes || []).length > 0 && (
                    <><span style={{ color: T.faint, fontWeight: 600 }}>Worth knowing</span>
                        <span style={{ color: T.ink }}>{proof.notes.map((n, i) => <span key={i} style={{ display: 'block', marginBottom: 4 }}>{n}</span>)}</span></>
                )}
            </div>
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, fontFamily: T.sans, fontSize: 12, fontWeight: 600, color: T.good }}>
                <Check size={13} />Every number here was calculated from your data{asOf ? ` as of ${new Date(asOf).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}, and the written answer was checked against it.
            </div>
        </div>
    );
}

// ── the result card ──
export function ResultCard({ result, sentence, secondary, onRun, onAsk, onPin, isPinned, followups, asOf }) {
    const [view, setView] = useState(0);
    const [proofOpen, setProofOpen] = useState(!secondary && false);
    const [copied, setCopied] = useState('');
    if (!result) return null;

    if (result.status === 'unavailable') {
        return (
            <div style={{ ...cardBase, border: '1px dashed color-mix(in srgb, var(--mom-charcoal) 18%, var(--mom-cream))', background: 'rgba(255,255,255,0.6)', padding: '22px 26px' }}>
                <InfoMark />
                <div style={{ fontFamily: T.sans, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.faint, margin: '12px 0 6px' }}>{result.title}</div>
                <p style={{ fontFamily: T.serif, fontSize: 17, lineHeight: 1.55, color: T.ink, margin: 0 }}>{sentence || result.reason}</p>
                {(result.followups || []).length > 0 && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>{result.followups.map((f) => <Chip key={f} size="sm" onClick={() => onAsk(f)}>{f}</Chip>)}</div>}
            </div>
        );
    }

    const views = result.views || [];
    const v = views[Math.min(view, views.length - 1)];
    const h = result.headline;
    const hp = h ? fmtParts(h.value, h.format, h.suffix) : null;
    const table = result.table;
    const people = table && table.kind === 'people' && table.rows.length > 0;
    const exportCsv = () => {
        const blob = new Blob([tableToCsv(table)], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${(result.title || 'insights').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    };
    const copyEmails = async () => {
        const emails = table.rows.map((r) => r.email).filter(Boolean).join(', ');
        try { await navigator.clipboard.writeText(emails); setCopied(`Copied ${table.rows.filter((r) => r.email).length} emails`); }
        catch (_) { setCopied('Copy failed'); }
        setTimeout(() => setCopied(''), 2400);
    };
    const lede = sentence || result.lede || result.summary;

    return (
        <div style={{ ...cardBase, animation: 'momInsRise .5s cubic-bezier(.16,1,.3,1) both' }}>
            <div style={{ padding: secondary ? '20px 26px 4px' : '26px 30px 6px', display: 'flex', gap: 28, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '0 0 auto', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: T.sans, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.faint }}>
                        {result.badgeKey && <MilestoneBadge badgeKey={result.badgeKey} size={28} />}
                        <span>{result.title}</span>
                    </div>
                    {hp && (
                        <div style={{ fontFamily: T.serif, fontWeight: 600, fontSize: secondary ? 44 : 62, letterSpacing: '-0.03em', lineHeight: 1, marginTop: 10, color: T.ink }}>
                            {hp.main}<span style={{ fontSize: secondary ? 20 : 26, color: T.soft, fontWeight: 500, letterSpacing: 0, marginLeft: 3 }}>{hp.unit}</span>
                            {h.label && !hp.unit && <span style={{ fontFamily: T.sans, fontSize: 14, color: T.faint, fontWeight: 500, letterSpacing: 0, marginLeft: 10 }}>{h.label}</span>}
                        </div>
                    )}
                    {result.delta && <div style={{ marginTop: 10 }}><DeltaPill {...result.delta} /></div>}
                </div>
                {lede && (
                    <p style={{ flex: '1 1 300px', fontFamily: T.serif, fontSize: secondary ? 15.5 : 18, lineHeight: 1.55, color: T.ink, margin: '22px 0 6px', maxWidth: 580 }}>{lede}</p>
                )}
            </div>

            {(result.stats || []).length > 0 && (
                <div style={{ display: 'flex', gap: 10, padding: secondary ? '14px 26px 0' : '16px 30px 0', flexWrap: 'wrap' }}>
                    {result.stats.map((s) => (
                        <div key={s.label} style={{ background: s.tone === 'warn' ? 'color-mix(in srgb, var(--mom-primary) 5%, #fff)' : T.well, border: `1px solid ${s.tone === 'warn' ? 'color-mix(in srgb, var(--mom-primary) 16%, transparent)' : T.hair}`, borderRadius: 16, padding: '10px 14px', minWidth: 104, flex: '1 1 0', boxSizing: 'border-box' }}>
                            <div style={{ fontFamily: T.sans, fontSize: 11, fontWeight: 600, color: T.faint, letterSpacing: '0.03em' }}>{s.label}</div>
                            <div style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 600, marginTop: 2, color: s.tone === 'warn' ? 'var(--mom-primary-dark, #c05a7a)' : s.tone === 'good' ? T.good : s.tone === 'bad' ? T.bad : T.ink }}>{fmt(s.value, s.format, s.suffix)}</div>
                        </div>
                    ))}
                </div>
            )}

            {views.length > 1 && (
                <div style={{ display: 'inline-flex', background: T.well, border: `1px solid ${T.hair}`, borderRadius: 12, padding: 3, margin: secondary ? '16px 0 0 26px' : '18px 0 0 30px' }}>
                    {views.map((vv, i) => (
                        <button key={vv.key} type="button" onClick={() => setView(i)} style={{ font: 'inherit', fontFamily: T.sans, fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 9, border: 'none', cursor: 'pointer', background: i === view ? '#fff' : 'transparent', color: i === view ? T.ink : T.soft, boxShadow: i === view ? T.shSm : 'none', transition: 'all .15s' }}>{vv.label}</button>
                    ))}
                </div>
            )}
            {v && v.chart && (
                <div key={v.key} style={{ padding: secondary ? '18px 22px 6px' : '20px 26px 6px', animation: 'momInsFade .25s ease both' }}>
                    {v.chart.caption && v.chart.type !== 'ranked' && <div style={{ fontFamily: T.sans, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.faint, marginBottom: 8 }}>{v.chart.caption}</div>}
                    <ChartFor chart={v.chart} />
                </div>
            )}

            {table && table.rows.length > 0 && <div style={{ marginTop: 12 }}><DataTable table={table} onPerson={(r) => onRun({ measure: 'member_profile', args: { query: r.uid } }, `Tell me about ${r.name}`)} /></div>}
            {table && table.rows.length === 0 && result.status === 'empty' && null}

            <div style={{ display: 'flex', gap: 8, padding: secondary ? '14px 26px 16px' : '14px 30px 18px', flexWrap: 'wrap', alignItems: 'center', borderTop: `1px solid ${T.hair}`, marginTop: 14 }}>
                {onPin && result.status === 'ok' && result.tile && (
                    <ActButton primary={!isPinned} disabled={isPinned} onClick={() => onPin(result)}><PinGlyph />{isPinned ? 'Pinned' : 'Pin to home'}</ActButton>
                )}
                {(result.actions || []).map((a) => <ActButton key={a.label} onClick={() => onRun(a.run, a.label)}>{a.label}</ActButton>)}
                {people && <ActButton onClick={copyEmails}>{copied || 'Copy emails'}</ActButton>}
                {table && table.rows.length > 0 && <ActButton onClick={exportCsv}>Export CSV</ActButton>}
                {result.proof && (
                    <button type="button" onClick={() => setProofOpen((o) => !o)} style={{ marginLeft: 'auto', font: 'inherit', fontFamily: T.sans, fontSize: 12.5, fontWeight: 600, color: proofOpen ? 'var(--mom-primary)' : T.soft, background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 4px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" /><path d="M9 12l2 2 4-4" /></svg>
                        How this was counted
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" style={{ transform: proofOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                    </button>
                )}
            </div>
            {proofOpen && <Proof proof={result.proof} asOf={asOf || result.asOf} />}
            {!secondary && (followups || []).length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '16px 30px 22px', borderTop: proofOpen ? 'none' : `1px solid ${T.hair}` }}>
                    {followups.map((f) => <Chip key={f} size="sm" onClick={() => onAsk(f)}>{f}</Chip>)}
                </div>
            )}
        </div>
    );
}

const cardBase = {
    background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
    border: '1px solid rgba(255,255,255,0.95)', borderRadius: 28, boxShadow: T.shMd, overflow: 'hidden',
};

const InfoMark = () => (
    <span style={{ width: 38, height: 38, borderRadius: 12, background: T.well, border: `1px solid ${T.line}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--mom-charcoal)" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.5" /></svg>
    </span>
);

export function ClarifyCard({ question, options, choices, onAsk, onRun }) {
    const badgeChoices = (choices || []).filter((c) => c.badgeKey);
    const otherChoices = (choices || []).filter((c) => !c.badgeKey);
    return (
        <div style={{ ...cardBase, padding: '22px 26px 24px', animation: 'momInsRise .45s cubic-bezier(.16,1,.3,1) both' }}>
            <p style={{ fontFamily: T.serif, fontSize: 18, lineHeight: 1.5, color: T.ink, margin: '0 0 16px' }}>{question}</p>
            {badgeChoices.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: 10, marginBottom: otherChoices.length ? 12 : 0 }}>
                    {badgeChoices.map((c) => <BadgeChoice key={c.badgeKey} choice={c} onRun={onRun} />)}
                </div>
            )}
            {otherChoices.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {otherChoices.map((c) => (
                        <Chip key={c.label + (c.sub || '')} onClick={() => onRun(c.run, c.label)}>
                            <span style={{ fontWeight: 600 }}>{c.label}</span>{c.sub && <span style={{ color: T.faint, marginLeft: 8, fontSize: 12 }}>{c.sub}</span>}
                        </Chip>
                    ))}
                </div>
            )}
            {(options || []).length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {options.map((o) => <Chip key={o} onClick={() => onAsk(o)}>{o}</Chip>)}
                </div>
            )}
        </div>
    );
}

function BadgeChoice({ choice, onRun }) {
    const [h, setH] = useState(false);
    return (
        <button type="button" onClick={() => onRun(choice.run, choice.label)} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
            style={{ font: 'inherit', background: '#fff', border: `1px solid ${h ? 'color-mix(in srgb, var(--mom-primary) 40%, transparent)' : T.line}`, borderRadius: 18, padding: '12px 8px 10px', cursor: 'pointer', textAlign: 'center', transform: h ? 'translateY(-2px)' : 'none', boxShadow: h ? T.shMd : 'none', transition: 'all .18s' }}>
            <span style={{ display: 'flex', justifyContent: 'center' }}><MilestoneBadge badgeKey={choice.badgeKey} size={50} /></span>
            <span style={{ display: 'block', fontFamily: T.sans, fontSize: 11.5, fontWeight: 600, color: T.ink, marginTop: 7, lineHeight: 1.3 }}>{choice.label}</span>
        </button>
    );
}

export function CannotCard({ reason, closest, suggestions, onAsk, onRun }) {
    return (
        <div style={{ ...cardBase, border: '1px dashed color-mix(in srgb, var(--mom-charcoal) 18%, var(--mom-cream))', background: 'rgba(255,255,255,0.6)', padding: '22px 26px', display: 'flex', gap: 16, animation: 'momInsRise .45s cubic-bezier(.16,1,.3,1) both' }}>
            <InfoMark />
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: T.serif, fontSize: 17.5, lineHeight: 1.55, color: T.ink, margin: 0 }}>{reason}</p>
                {closest && closest.status === 'ok' && (
                    <p style={{ fontFamily: T.sans, fontSize: 14, lineHeight: 1.6, color: T.soft, margin: '10px 0 0' }}>
                        Closest thing I can tell you exactly: <b style={{ color: T.ink }}>{closest.lede || closest.summary}</b>{' '}
                        <button type="button" onClick={() => onRun({ measure: closest.measure, args: closest.args }, closest.title)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'var(--mom-primary)' }}>Open it →</button>
                    </p>
                )}
                {(suggestions || []).length > 0 && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>{suggestions.map((s) => <Chip key={s} size="sm" onClick={() => onAsk(s)}>{s}</Chip>)}</div>}
            </div>
        </div>
    );
}
